/**
 * MobileTeacherOverview — My schedule on a phone: the "Your week ahead" card,
 * then the teacher's upcoming workshops. The greeting comes from the teacher
 * shell's header, so the page carries none of its own. A workshop open for
 * check-in gets a Check in button under it that opens the check-in modal here.
 */
import { Users } from 'lucide-react';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { viewerDate } from '../../../utils/viewer-date';
import { WorkshopList, useCancelledFilter, type WorkshopChip } from '../primitives';
import { BucketError, BucketLoading } from '../regional/BucketStates';
import { useMyWorkshops, type MyWorkshop } from '../teacher/use-my-workshops';
import { teacherChipDetailUrl } from '../workshopDetailUrlFor';
import { acceptsCheckIn, isCheckInOpen } from '../workshops/detail/checkInModel';

import MyScheduleCheckIn from './components/MyScheduleCheckIn';
import { MyUpcomingHeading } from './components/MyUpcomingHeading';
import { HeroCard } from './MobileTeacherHero';

/** How far ahead the upcoming list reaches — the longest range the API serves. */
const UPCOMING_DAYS = 100;

export default function MobileTeacherOverview() {
  const navigate = useNavigate();
  const [now] = useState(() => new Date());
  const [checkingIn, setCheckingIn] = useState<string | null>(null);
  // From the start of today, so a workshop already under way keeps its Check
  // in button until the day is over.
  const q = useMyWorkshops(viewerDate(0, now), viewerDate(UPCOMING_DAYS - 1, now));
  const cancelled = useCancelledFilter(q.data ?? []);

  if (q.isLoading) {
    return (
      <div>
        <BucketLoading />
      </div>
    );
  }
  if (q.isError) {
    return (
      <div>
        <BucketError error={q.error} />
      </div>
    );
  }

  const workshops = q.data ?? [];
  // Only today's workshops get a Check in button here; the detail page offers
  // check-in at any time.
  const canCheckIn = (w: MyWorkshop) =>
    acceptsCheckIn(w.lifecycle) && isCheckInOpen(w.checkInOpensAt, now);
  const checkable = workshops.filter(canCheckIn);
  const rest = cancelled.shown.filter((w) => !canCheckIn(w));
  const openDetail = (w: WorkshopChip) => navigate(teacherChipDetailUrl(w));
  const list = (items: WorkshopChip[], emptyLabel?: string) => (
    <WorkshopList
      workshops={items}
      size="sm"
      showTag={false}
      showNames={false}
      onWorkshopClick={openDetail}
      emptyLabel={emptyLabel}
    />
  );

  return (
    <div>
      {/* The week ahead counts only workshops that will run. */}
      <HeroCard workshops={workshops.filter((w) => acceptsCheckIn(w.lifecycle))} now={now} />
      <MyUpcomingHeading cancelled={cancelled} />
      <div className="flex flex-col gap-2">
        {checkable.map((w) => (
          <div key={w.id} className="flex flex-col gap-2">
            {list([w])}
            <button
              type="button"
              className="nb-btn nb-btn-primary"
              style={{ width: '100%', justifyContent: 'center', gap: 8 }}
              onClick={() => setCheckingIn(w.id)}
            >
              <Users size={16} aria-hidden />
              Check in · {w.venueName}
            </button>
          </div>
        ))}
        {(rest.length > 0 || checkable.length === 0) && list(rest, 'No upcoming workshops.')}
      </div>
      {checkingIn && (
        <MyScheduleCheckIn workshopId={checkingIn} onClose={() => setCheckingIn(null)} />
      )}
    </div>
  );
}
